/**
 * IntakeFormBuilder
 *
 * Two-pane form authoring UI: edit panel on the left, live preview on the right.
 * Sections and questions are reordered via up/down buttons (no DnD dep).
 * Saves draft and publishes via intakeFormTemplateService.
 */

import React, { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, ChevronUp, ChevronDown, Eye, EyeOff } from 'lucide-react';
import type {
  IntakeSection,
  IntakeQuestion,
  QuestionInputType,
  MapToField,
  ValidationRule,
} from '@/lib/intake/templates/types';
import { IntakeFormRenderer } from './IntakeFormRenderer';
import type { IntakeFormTemplateSchema } from '@/services/intakeFormTemplateService';

const QUESTION_TYPES: { value: QuestionInputType; label: string }[] = [
  { value: 'text', label: 'Short text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'select', label: 'Single choice' },
  { value: 'multiselect', label: 'Multiple choice' },
  { value: 'scale', label: 'Scale' },
  { value: 'rating', label: 'Rating (stars)' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time' },
  { value: 'datetime', label: 'Date & time' },
  { value: 'address', label: 'Address' },
  { value: 'phone', label: 'Phone number' },
  { value: 'file', label: 'File upload' },
];

const NONE_SENTINEL = '__none__';

const MAP_TO_OPTIONS: { value: string; label: string }[] = [
  { value: NONE_SENTINEL, label: 'No — store only in response' },
  { value: 'patient.first_name', label: 'First name' },
  { value: 'patient.middle_name', label: 'Middle name' },
  { value: 'patient.last_name', label: 'Last name' },
  { value: 'patient.dob', label: 'Date of birth' },
  { value: 'patient.gender', label: 'Gender' },
  { value: 'patient.phone', label: 'Phone number' },
  { value: 'patient.email', label: 'Email' },
  { value: 'patient.allergies', label: 'Patient allergies' },
  { value: 'patient.medical_history', label: 'Medical history' },
  { value: 'patient.emergency_contact', label: 'Emergency contact' },
  { value: 'patient.billing_address', label: 'Billing address' },
  { value: 'patient.shipping_address', label: 'Shipping address' },
];

const newQuestion = (): IntakeQuestion => ({
  id: `q_${Math.random().toString(36).slice(2, 10)}`,
  text: 'New question',
  inputType: 'text',
  required: false,
});

const newSection = (): IntakeSection => ({
  id: `s_${Math.random().toString(36).slice(2, 10)}`,
  title: 'New section',
  description: '',
  questions: [newQuestion()],
});

interface BuilderProps {
  initialName?: string;
  initialDescription?: string;
  initialEstimatedMinutes?: number | null;
  initialSchema?: IntakeFormTemplateSchema;
  initialIsPublished?: boolean;
  saving?: boolean;
  onSave: (payload: {
    name: string;
    description: string;
    estimated_minutes: number | null;
    schema: IntakeFormTemplateSchema;
    is_published: boolean;
  }) => Promise<void> | void;
  onCancel?: () => void;
}

export const IntakeFormBuilder: React.FC<BuilderProps> = ({
  initialName = '',
  initialDescription = '',
  initialEstimatedMinutes = null,
  initialSchema = { sections: [newSection()] },
  initialIsPublished = false,
  saving = false,
  onSave,
  onCancel,
}) => {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [estimatedMinutes, setEstimatedMinutes] = useState<number | null>(initialEstimatedMinutes);
  const [schema, setSchema] = useState<IntakeFormTemplateSchema>(
    initialSchema && initialSchema.sections?.length
      ? initialSchema
      : { sections: [newSection()] },
  );
  const [isPublished, setIsPublished] = useState(initialIsPublished);
  const [previewResponses, setPreviewResponses] = useState<Record<string, any>>({});
  const [showPreview, setShowPreview] = useState(true);

  const setSections = (next: IntakeSection[]) => setSchema({ sections: next });

  const moveSection = (i: number, dir: -1 | 1) => {
    const next = [...schema.sections];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setSections(next);
  };

  const removeSection = (i: number) => {
    if (schema.sections.length <= 1) return;
    setSections(schema.sections.filter((_, idx) => idx !== i));
  };

  const updateSection = (i: number, patch: Partial<IntakeSection>) => {
    setSections(schema.sections.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  };

  const addSection = () => setSections([...schema.sections, newSection()]);

  const moveQuestion = (si: number, qi: number, dir: -1 | 1) => {
    const next = [...schema.sections];
    const qs = [...next[si].questions];
    const j = qi + dir;
    if (j < 0 || j >= qs.length) return;
    [qs[qi], qs[j]] = [qs[j], qs[qi]];
    next[si] = { ...next[si], questions: qs };
    setSections(next);
  };

  const removeQuestion = (si: number, qi: number) => {
    const next = [...schema.sections];
    next[si] = { ...next[si], questions: next[si].questions.filter((_, idx) => idx !== qi) };
    setSections(next);
  };

  const updateQuestion = (si: number, qi: number, patch: Partial<IntakeQuestion>) => {
    const next = [...schema.sections];
    next[si] = {
      ...next[si],
      questions: next[si].questions.map((q, idx) => (idx === qi ? { ...q, ...patch } : q)),
    };
    setSections(next);
  };

  const addQuestion = (si: number) => {
    const next = [...schema.sections];
    next[si] = { ...next[si], questions: [...next[si].questions, newQuestion()] };
    setSections(next);
  };

  const canSave = name.trim().length > 0 && schema.sections.every(s => s.title.trim().length > 0);

  const handleSave = async () => {
    if (!canSave || saving) return;
    await onSave({
      name: name.trim(),
      description: description.trim(),
      estimated_minutes: estimatedMinutes,
      schema,
      is_published: isPublished,
    });
  };

  const allQuestions = useMemo(
    () => schema.sections.flatMap(s => s.questions.map(q => ({
      id: q.id,
      text: q.text,
      inputType: q.inputType,
      options: q.options,
    }))),
    [schema],
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Editor */}
      <div className="space-y-6 min-w-0">
        {/* Top-level metadata */}
        <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-4">
          <div className="space-y-1.5">
            <Label htmlFor="form-name" className="required">Form name</Label>
            <Input
              id="form-name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. New Patient Intake"
              maxLength={200}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="form-desc">Description</Label>
            <Textarea
              id="form-desc"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Shown to patients on the intake landing page."
              rows={2}
              maxLength={1000}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="form-eta">Estimated minutes</Label>
              <Input
                id="form-eta"
                type="number"
                value={estimatedMinutes ?? ''}
                onChange={e => setEstimatedMinutes(e.target.value === '' ? null : Number(e.target.value))}
                min={1}
                max={120}
              />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <Switch checked={isPublished} onCheckedChange={setIsPublished} />
              <Label className="font-normal">Published (visible in booking)</Label>
            </div>
          </div>
        </div>

        {/* Sections */}
        {schema.sections.map((section, si) => (
          <div key={section.id} className="space-y-4 rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex items-start gap-2">
              <div className="flex-1 space-y-3">
                <div className="space-y-1.5">
                  <Label className="required text-xs uppercase tracking-wide text-slate-500">Section title</Label>
                  <Input
                    value={section.title}
                    onChange={e => updateSection(si, { title: e.target.value })}
                    maxLength={200}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-slate-500">Section description</Label>
                  <Input
                    value={section.description || ''}
                    onChange={e => updateSection(si, { description: e.target.value })}
                    placeholder="Optional helper text shown above questions"
                    maxLength={200}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <Button variant="ghost" size="icon" onClick={() => moveSection(si, -1)} disabled={si === 0}><ChevronUp className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => moveSection(si, 1)} disabled={si === schema.sections.length - 1}><ChevronDown className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => removeSection(si)} disabled={schema.sections.length === 1}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            </div>

            <div className="space-y-3 border-l-2 border-slate-100 pl-4">
              {section.questions.map((q, qi) => (
                <QuestionEditor
                  key={q.id}
                  question={q}
                  allQuestions={allQuestions.filter(other => other.id !== q.id)}
                  onChange={patch => updateQuestion(si, qi, patch)}
                  onMoveUp={qi === 0 ? undefined : () => moveQuestion(si, qi, -1)}
                  onMoveDown={qi === section.questions.length - 1 ? undefined : () => moveQuestion(si, qi, 1)}
                  onRemove={section.questions.length <= 1 ? undefined : () => removeQuestion(si, qi)}
                />
              ))}
              <Button variant="outline" size="sm" onClick={() => addQuestion(si)}>
                <Plus className="h-3 w-3 mr-1" /> Add question
              </Button>
            </div>
          </div>
        ))}

        <Button variant="outline" onClick={addSection}>
          <Plus className="h-4 w-4 mr-1" /> Add section
        </Button>

        <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-white border-t border-slate-200 flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => setShowPreview(p => !p)}>
            {showPreview ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
            {showPreview ? 'Hide' : 'Show'} preview
          </Button>
          {onCancel && <Button variant="outline" onClick={onCancel}>Cancel</Button>}
          <Button onClick={handleSave} disabled={!canSave || saving}>
            {saving ? 'Saving…' : isPublished ? 'Save & publish' : 'Save draft'}
          </Button>
        </div>
      </div>

      {/* Preview */}
      {showPreview && (
        <div className="min-w-0 rounded-lg border border-slate-200 bg-slate-50 p-6 lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-auto">
          <p className="mb-4 text-xs uppercase tracking-wide text-slate-500">Patient preview</p>
          {name && <h1 className="text-xl font-semibold mb-1">{name}</h1>}
          {description && <p className="text-sm text-slate-600 mb-6">{description}</p>}
          <IntakeFormRenderer
            sections={schema.sections}
            responses={previewResponses}
            onChange={(qid, v) => setPreviewResponses(prev => ({ ...prev, [qid]: v }))}
          />
        </div>
      )}
    </div>
  );
};

// ─── Question editor row ─────────────────────────────────────────────────────

const QuestionEditor: React.FC<{
  question: IntakeQuestion;
  allQuestions: { id: string; text: string; inputType: QuestionInputType; options?: string[] }[];
  onChange: (patch: Partial<IntakeQuestion>) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onRemove?: () => void;
}> = ({ question, allQuestions, onChange, onMoveUp, onMoveDown, onRemove }) => {
  const [expanded, setExpanded] = useState(false);
  const showOptions = question.inputType === 'select' || question.inputType === 'multiselect';
  const isNumber = question.inputType === 'number';
  const isText = question.inputType === 'text';
  const isScale = question.inputType === 'scale';
  const isRating = question.inputType === 'rating';
  const isFile = question.inputType === 'file';

  // Email is stored as a `pattern` rule with this canonical regex so the
  // checkbox can be a pure toggle — recognising it on load and removing the
  // exact same entry on uncheck.
  const EMAIL_PATTERN = '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$';

  // Hold the options textarea as a raw string so blank lines / trailing
  // spaces survive keystrokes — recomputing it from `options.join('\n')` on
  // every change would filter out the empty line the user just pressed Enter
  // on, snapping the cursor back to the previous line. Resync only when we
  // switch to a different question.
  const [optionsText, setOptionsText] = useState(() => (question.options || []).join('\n'));
  const lastSyncedIdRef = React.useRef(question.id);
  if (lastSyncedIdRef.current !== question.id) {
    lastSyncedIdRef.current = question.id;
    setOptionsText((question.options || []).join('\n'));
  }

  const numberRule = (type: 'min' | 'max'): number | '' => {
    const r = (question.validationRules || []).find(x => x.type === type);
    return r && r.value !== undefined && r.value !== '' ? Number(r.value) : '';
  };

  const setNumberRule = (type: 'min' | 'max', raw: string) => {
    const others: ValidationRule[] = (question.validationRules || []).filter(x => x.type !== type);
    if (raw === '') return onChange({ validationRules: others.length ? others : undefined });
    const num = Number(raw);
    if (Number.isNaN(num)) return;
    const message = type === 'min'
      ? `Must be at least ${num}`
      : `Must be at most ${num}`;
    onChange({ validationRules: [...others, { type, value: num, message }] });
  };

  const lengthRule = (type: 'minLength' | 'maxLength'): number | '' => {
    const r = (question.validationRules || []).find(x => x.type === type);
    return r && r.value !== undefined && r.value !== '' ? Number(r.value) : '';
  };

  const setLengthRule = (type: 'minLength' | 'maxLength', raw: string) => {
    const others: ValidationRule[] = (question.validationRules || []).filter(x => x.type !== type);
    if (raw === '') return onChange({ validationRules: others.length ? others : undefined });
    const num = Number(raw);
    if (Number.isNaN(num) || num < 0) return;
    const message = type === 'minLength'
      ? `Must be at least ${num} characters`
      : `Must be at most ${num} characters`;
    onChange({ validationRules: [...others, { type, value: num, message }] });
  };

  const isEmail = (question.validationRules || []).some(
    r => r.type === 'pattern' && r.value === EMAIL_PATTERN,
  );

  const setIsEmail = (on: boolean) => {
    const others: ValidationRule[] = (question.validationRules || []).filter(
      x => !(x.type === 'pattern' && x.value === EMAIL_PATTERN),
    );
    if (!on) return onChange({ validationRules: others.length ? others : undefined });
    onChange({
      validationRules: [
        ...others,
        { type: 'pattern', value: EMAIL_PATTERN, message: 'Must be a valid email address' },
      ],
    });
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 space-y-3">
      <div className="flex items-start gap-2">
        <div className="flex-1 grid grid-cols-12 gap-2">
          <div className="col-span-12 md:col-span-7 space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Question text</Label>
            <Input
              value={question.text}
              onChange={e => onChange({ text: e.target.value })}
            />
          </div>
          <div className="col-span-12 md:col-span-5 space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Type</Label>
            <Select
              value={question.inputType}
              onValueChange={(v: QuestionInputType) => onChange({ inputType: v })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {QUESTION_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <Button variant="ghost" size="icon" onClick={onMoveUp} disabled={!onMoveUp}><ChevronUp className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" onClick={onMoveDown} disabled={!onMoveDown}><ChevronDown className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" onClick={onRemove} disabled={!onRemove}><Trash2 className="h-4 w-4 text-red-500" /></Button>
        </div>
      </div>

      {showOptions && (
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-slate-500">Options (one per line)</Label>
          <Textarea
            value={optionsText}
            onChange={e => {
              const raw = e.target.value;
              setOptionsText(raw);
              onChange({ options: raw.split('\n').map(s => s.trim()).filter(Boolean) });
            }}
            rows={3}
            placeholder={'Option A\nOption B'}
          />
        </div>
      )}

      {isNumber && (
        <div className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-4 space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Min</Label>
            <Input
              type="number"
              step={question.allowDecimals ? 'any' : 1}
              value={numberRule('min')}
              onChange={e => setNumberRule('min', e.target.value)}
              placeholder="No minimum"
            />
          </div>
          <div className="col-span-4 space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Max</Label>
            <Input
              type="number"
              step={question.allowDecimals ? 'any' : 1}
              value={numberRule('max')}
              onChange={e => setNumberRule('max', e.target.value)}
              placeholder="No maximum"
            />
          </div>
          <div className="col-span-4 flex items-center gap-2 pb-2">
            <Checkbox
              id={`dec-${question.id}`}
              checked={!!question.allowDecimals}
              onCheckedChange={c => onChange({ allowDecimals: !!c })}
            />
            <Label htmlFor={`dec-${question.id}`} className="font-normal cursor-pointer">
              Allow decimals
            </Label>
          </div>
        </div>
      )}

      {isFile && (
        <div className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-7 space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Allowed file types</Label>
            <Select
              value={question.fileTypes || 'any'}
              onValueChange={v => onChange({ fileTypes: v as 'any' | 'image' | 'pdf' | 'document' })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any (images, PDF, DOC)</SelectItem>
                <SelectItem value="image">Images only (JPEG, PNG, WebP, HEIC)</SelectItem>
                <SelectItem value="pdf">PDF only</SelectItem>
                <SelectItem value="document">Documents (PDF, DOC, DOCX)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-5 space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Max size (MB, up to 25)</Label>
            <Input
              type="number"
              min={1}
              max={25}
              value={question.fileMaxSizeMb ?? ''}
              onChange={e => {
                const raw = e.target.value;
                if (raw === '') return onChange({ fileMaxSizeMb: undefined });
                const num = Number(raw);
                if (Number.isNaN(num)) return;
                onChange({ fileMaxSizeMb: Math.min(25, Math.max(1, Math.trunc(num))) });
              }}
              placeholder="15"
            />
          </div>
        </div>
      )}

      {isRating && (
        <div className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-6 space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Rating max (up to 10)</Label>
            <Input
              type="number"
              min={1}
              max={10}
              value={question.ratingMax ?? ''}
              onChange={e => {
                const raw = e.target.value;
                if (raw === '') return onChange({ ratingMax: undefined });
                const num = Number(raw);
                if (Number.isNaN(num)) return;
                onChange({ ratingMax: Math.min(10, Math.max(1, Math.trunc(num))) });
              }}
              placeholder="5"
            />
          </div>
        </div>
      )}

      {isScale && (
        <div className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-6 space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Scale min</Label>
            <Input
              type="number"
              min={0}
              max={20}
              value={question.scaleMin ?? ''}
              onChange={e => {
                const raw = e.target.value;
                if (raw === '') return onChange({ scaleMin: undefined });
                const num = Number(raw);
                if (Number.isNaN(num)) return;
                onChange({ scaleMin: Math.min(20, Math.max(0, Math.trunc(num))) });
              }}
              placeholder="1"
            />
          </div>
          <div className="col-span-6 space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Scale max (up to 20)</Label>
            <Input
              type="number"
              min={1}
              max={20}
              value={question.scaleMax ?? ''}
              onChange={e => {
                const raw = e.target.value;
                if (raw === '') return onChange({ scaleMax: undefined });
                const num = Number(raw);
                if (Number.isNaN(num)) return;
                onChange({ scaleMax: Math.min(20, Math.max(1, Math.trunc(num))) });
              }}
              placeholder="5"
            />
          </div>
        </div>
      )}

      {isText && (
        <div className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-4 space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Min length</Label>
            <Input
              type="number"
              min={0}
              value={lengthRule('minLength')}
              onChange={e => setLengthRule('minLength', e.target.value)}
              placeholder="No minimum"
            />
          </div>
          <div className="col-span-4 space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Max length</Label>
            <Input
              type="number"
              min={0}
              value={lengthRule('maxLength')}
              onChange={e => setLengthRule('maxLength', e.target.value)}
              placeholder="No maximum"
            />
          </div>
          <div className="col-span-4 flex items-center gap-2 pb-2">
            <Checkbox
              id={`email-${question.id}`}
              checked={isEmail}
              onCheckedChange={c => setIsEmail(!!c)}
            />
            <Label htmlFor={`email-${question.id}`} className="font-normal cursor-pointer">
              Email format
            </Label>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id={`req-${question.id}`}
            checked={!!question.required}
            onCheckedChange={c => onChange({ required: !!c })}
          />
          <Label htmlFor={`req-${question.id}`} className="font-normal cursor-pointer">Required</Label>
        </div>
        <button
          type="button"
          className="text-xs text-brand-teal hover:underline"
          onClick={() => setExpanded(e => !e)}
        >
          {expanded ? 'Hide' : 'More'} options
        </button>
      </div>

      {expanded && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-slate-100">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Help text</Label>
            <Input
              value={question.helpText || ''}
              onChange={e => onChange({ helpText: e.target.value })}
              placeholder="Shown beneath the question"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Map answer to patient field</Label>
            <Select
              value={question.mapTo || NONE_SENTINEL}
              onValueChange={v => onChange({ mapTo: v === NONE_SENTINEL ? undefined : (v as MapToField) })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {MAP_TO_OPTIONS.map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Show only when (optional)</Label>
            <div className="grid grid-cols-12 gap-2">
              <Select
                value={question.conditionalOn?.questionId || NONE_SENTINEL}
                onValueChange={v => onChange({
                  conditionalOn: v && v !== NONE_SENTINEL
                    ? { questionId: v, operator: 'equals', value: '' }
                    : undefined,
                })}
              >
                <SelectTrigger className="col-span-5"><SelectValue placeholder="No condition" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_SENTINEL}>No condition</SelectItem>
                  {allQuestions.map(q => (
                    <SelectItem key={q.id} value={q.id}>{q.text.trim() || q.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={question.conditionalOn?.operator || 'equals'}
                onValueChange={v => onChange({
                  conditionalOn: question.conditionalOn
                    ? { ...question.conditionalOn, operator: v as any }
                    : undefined,
                })}
                disabled={!question.conditionalOn}
              >
                <SelectTrigger className="col-span-3"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="equals">equals</SelectItem>
                  <SelectItem value="notEquals">not equals</SelectItem>
                  <SelectItem value="contains">contains</SelectItem>
                  <SelectItem value="greaterThan">greater than</SelectItem>
                  <SelectItem value="lessThan">less than</SelectItem>
                </SelectContent>
              </Select>
              {(() => {
                const sourceQ = allQuestions.find(q => q.id === question.conditionalOn?.questionId);
                const setValue = (v: string | number | boolean) => onChange({
                  conditionalOn: question.conditionalOn
                    ? { ...question.conditionalOn, value: v }
                    : undefined,
                });
                if (sourceQ?.inputType === 'boolean') {
                  // Boolean answers are stored as actual true/false at runtime,
                  // so the rule value must also be boolean — a string "Yes"
                  // would never match.
                  const current = question.conditionalOn?.value;
                  const sentinel = current === true ? 'yes' : current === false ? 'no' : '';
                  return (
                    <Select
                      value={sentinel || undefined}
                      onValueChange={v => setValue(v === 'yes')}
                      disabled={!question.conditionalOn}
                    >
                      <SelectTrigger className="col-span-4">
                        <SelectValue placeholder="Yes / No" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  );
                }
                if ((sourceQ?.inputType === 'select' || sourceQ?.inputType === 'multiselect') && (sourceQ.options || []).length > 0) {
                  return (
                    <Select
                      value={String(question.conditionalOn?.value ?? '') || undefined}
                      onValueChange={v => setValue(v)}
                      disabled={!question.conditionalOn}
                    >
                      <SelectTrigger className="col-span-4">
                        <SelectValue placeholder="Pick an option" />
                      </SelectTrigger>
                      <SelectContent>
                        {(sourceQ.options || []).map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  );
                }
                const isNumeric = sourceQ?.inputType === 'number' || sourceQ?.inputType === 'scale';
                return (
                  <Input
                    className="col-span-4"
                    type={isNumeric ? 'number' : 'text'}
                    value={String(question.conditionalOn?.value ?? '')}
                    onChange={e => {
                      const raw = e.target.value;
                      setValue(isNumeric && raw !== '' ? Number(raw) : raw);
                    }}
                    placeholder="value"
                    disabled={!question.conditionalOn}
                  />
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntakeFormBuilder;
